import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbDatepickerKeyboardService,
  NgbDateStruct
} from '@ng-bootstrap/ng-bootstrap';
import { DatetimeService } from '../../../../services/datetime/datetime.service';
import { CustomAdapter } from '../../../../services/NgbDatepicker/custom-adapter.service';
import { CustomDateParserFormatter } from '../../../../services/NgbDatepicker/custom-dateparser-formatter.service';
import { CustomDatepickerI18n } from '../../../../services/NgbDatepicker/custom-datepicker-i18n.service';
import { CustomKeyboardService } from '../../../../services/NgbDatepicker/custom-keyboard-service';

export const DashboardDayPeriod = {
  Today: 0,
  Tomorrow: 1,
  Yesterday: 2,
  Custom: 3
} as const;

export type DashboardDayPeriod = typeof DashboardDayPeriod[keyof typeof DashboardDayPeriod];

export interface DashboardDate {
  period: DashboardDayPeriod;
  dateDMY: string;
  dateYMD: string;
  dateUnixTime: number;
}

@Component({
  selector: 'dashboard-day-filter',
  templateUrl: './dashboard-day-filter.component.html',
  styleUrls: ['./dashboard-day-filter.component.scss'],
  providers: [
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
    { provide: NgbDateAdapter, useClass: CustomAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    { provide: NgbDatepickerKeyboardService, useClass: CustomKeyboardService }
  ]
})
export class DashboardDayFilterComponent implements OnInit, OnChanges {
  @Input() id: string = 'dashboard-day-filter';
  @Input() filterDate: string = '';
  @Input() disabled: boolean = false;
  @Input() class: string = '';
  @Input() sm: boolean = true;

  @Output() onDateChange = new EventEmitter<DashboardDate>();

  period: DashboardDayPeriod = DashboardDayPeriod.Today;
  minDate: NgbDateStruct;
  maxDate: NgbDateStruct;
  private initialized: boolean = false;

  constructor(
    private DatetimeService: DatetimeService,
    private DatepickerI18n: NgbDatepickerI18n
  ) {
    const today = new Date();
    this.minDate = this.getDateStruct(this.addDays(today, -7));
    this.maxDate = this.getDateStruct(this.addDays(today, 7));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(!this.initialized || !changes.filterDate || !this.dateIsValid())
      return;

    this.updatePeriod();
    this.emitDateChange();
  }

  ngOnInit(): void {
    if(!this.dateIsValid())
      this.filterDate = this.DatetimeService.getCurrentDateDMY();

    this.updatePeriod();
    this.initialized = true;
    Promise.resolve().then(() => this.emitDateChange());
  }

  get dayLabelTranslation(): string | null {
    switch(this.period) {
      case DashboardDayPeriod.Today: return 'ui.calendar-today';
      case DashboardDayPeriod.Tomorrow: return 'ui.calendar-tomorrow';
      case DashboardDayPeriod.Yesterday: return 'ui.calendar-yesterday';
      default: return null;
    }
  }

  get weekdayLabel(): string {
    if(!this.dateIsValid())
      return '';

    const parts = this.DatetimeService.getDatePartsDMY(this.filterDate);
    const date = new Date(parts.year, parts.month - 1, parts.day);
    const datepickerWeekday = (date.getDay() + 6) % 7 + 1;
    return this.DatepickerI18n.getWeekdayLabel(datepickerWeekday);
  }

  dateChange(): void {
    if(this.disabled || !this.dateIsValid())
      return;

    this.updatePeriod();
    this.emitDateChange();
  }

  dateIsValid(): boolean {
    if(!this.filterDate || !this.DatetimeService.validateDateStr(this.filterDate))
      return false;

    const dateYMD = this.DatetimeService.dateDMYToYMD(this.filterDate);
    const today = new Date();
    const minDateYMD = this.getDateYMD(this.addDays(today, -7));
    const maxDateYMD = this.getDateYMD(this.addDays(today, 7));
    return dateYMD >= minDateYMD && dateYMD <= maxDateYMD;
  }

  private updatePeriod(): void {
    const selectedDateYMD = this.DatetimeService.dateDMYToYMD(this.filterDate);
    const today = new Date();
    const todayYMD = this.getDateYMD(today);
    const yesterdayYMD = this.getDateYMD(this.addDays(today, -1));
    const tomorrowYMD = this.getDateYMD(this.addDays(today, 1));

    if(selectedDateYMD === todayYMD)
      this.period = DashboardDayPeriod.Today;
    else if(selectedDateYMD === tomorrowYMD)
      this.period = DashboardDayPeriod.Tomorrow;
    else if(selectedDateYMD === yesterdayYMD)
      this.period = DashboardDayPeriod.Yesterday;
    else
      this.period = DashboardDayPeriod.Custom;
  }

  private emitDateChange(): void {
    if(!this.dateIsValid())
      return;

    const parts = this.DatetimeService.getDatePartsDMY(this.filterDate);
    const date = new Date(parts.year, parts.month - 1, parts.day, 0, 0, 0, 0);

    this.onDateChange.emit({
      period: this.period,
      dateDMY: this.filterDate,
      dateYMD: this.DatetimeService.dateDMYToYMD(this.filterDate),
      dateUnixTime: date.getTime()
    });
  }

  private getDateYMD(date: Date): string {
    return this.DatetimeService.dateDMYToYMD(this.DatetimeService.getDateToDMY(date));
  }

  private getDateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate()
    };
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    result.setDate(result.getDate() + days);
    return result;
  }
}
